import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Plus, ClipboardList, MoreHorizontal, Trash2 } from "lucide-react";
import { AppLayout } from "@/components/app-layout";
import { LoadingOverlay } from "@/components/loading-overlay";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { NewChoreDialog } from "@/components/new-chore-dialog";
import { getChoreSchedule, listChores, deleteChore } from "@repo/api/client";
import type { Chore } from "@repo/api/client";
import { BackLink } from "@/components/back-link";
import { EventPropagationBoundary } from "@/components/event-propagation-boundary";

export function ChoreScheduleDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [dialogOpen, setDialogOpen] = useState(false);

  if (typeof id !== "string") {
    throw new Error("`id` param is undefined");
  }

  const {
    data: schedule,
    isPending: scheduleLoading,
    isError: scheduleError,
  } = useQuery({
    queryKey: ["chore-schedules", id],
    queryFn: () => getChoreSchedule(id),
  });

  const {
    data: chores = [],
    isPending: choresLoading,
    isError: choresError,
  } = useQuery({
    queryKey: ["chores", id],
    queryFn: () => listChores(id),
  });

  return (
    <LoadingOverlay isLoading={scheduleLoading || choresLoading}>
      <AppLayout title={schedule?.name}>
        <div className="px-4 pt-6 space-y-3">
          <BackLink text="Schedules" to="/chore-schedules" />
          {(scheduleError || choresError) && (
            <p className="text-sm text-destructive">Failed to load data.</p>
          )}

          {chores.map((chore) => (
            <ChoreListItem key={chore.id} chore={chore} />
          ))}

          <Button onClick={() => setDialogOpen(true)} className="w-full" variant="outline">
            <Plus size={16} />
            New Chore
          </Button>
        </div>

        <NewChoreDialog choreScheduleId={id} open={dialogOpen} onOpenChange={setDialogOpen} />
      </AppLayout>
    </LoadingOverlay>
  );
}

function ChoreListItem({ chore }: { chore: Chore }) {
  const navigate = useNavigate();

  const choreUrl = `/chore-schedules/${chore.choreScheduleId}/chores/${chore.id}`;

  return (
    <Button
      asChild
      variant="outline"
      className="w-full"
      onClick={() => navigate(choreUrl)}
      onKeyDown={(e) => e.key === "Enter" && navigate(choreUrl)}
    >
      <div data-href={choreUrl} tabIndex={0} role="link">
        <ClipboardList size={20} className="shrink-0 text-muted-foreground" />
        <span className="flex-1 font-medium text-left">{chore.name}</span>
        <EventPropagationBoundary filter={{ onClick: true, onKeyDown: true }}>
          <ChoreActionsMenu chore={chore} />
        </EventPropagationBoundary>
      </div>
    </Button>
  );
}

function ChoreActionsMenu({ chore }: { chore: Chore }) {
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="-mr-2 h-8 w-8">
          <MoreHorizontal size={16} />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-40 p-1" align="end">
        <DeleteChoreButton chore={chore} onSuccess={() => setOpen(false)} />
      </PopoverContent>
    </Popover>
  );
}

function DeleteChoreButton({ chore, onSuccess }: { chore: Chore; onSuccess: () => void }) {
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: () => deleteChore(chore.choreScheduleId, chore.id),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["chores", chore.choreScheduleId],
      });
      onSuccess();
    },
  });

  return (
    <>
      <Button
        onClick={() => mutation.mutate()}
        disabled={mutation.isPending}
        variant="destructive"
        className="w-full"
      >
        <Trash2 size={14} />
        {mutation.isPending ? "Deleting…" : "Delete"}
      </Button>
      {mutation.isError && <p className="px-2 py-1 text-xs text-destructive">Failed to delete.</p>}
    </>
  );
}
